import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import Modal from './Modal';
import Picture from './Picture';

const DuplicatesModal = ({ isOpen, onClose, username, duplicates }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);

  const { paginatedDuplicates, chartData } = useMemo(() => {
    // Préparation des données pour l'histogramme (365 derniers jours)
    const today = new Date();
    const yearAgo = new Date(today.setFullYear(today.getFullYear() - 1));
    const duplicatesByDay = {};

    duplicates.forEach(duplicate => {
      const date = new Date(duplicate.createdat);
      if (date >= yearAgo) {
        const dateStr = date.toISOString().split('T')[0];
        duplicatesByDay[dateStr] = (duplicatesByDay[dateStr] || 0) + 1;
      }
    });

    const chartData = Object.entries(duplicatesByDay)
      .map(([date, count]) => ({
        date,
        count
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    let filteredDuplicates;
    if (selectedDate) {
      // Trouver d'abord tous les CRC des photos du jour sélectionné
      const selectedDateCRCs = new Set(
        duplicates
          .filter(d => new Date(d.createdat).toISOString().split('T')[0] === selectedDate)
          .map(d => d.picture_crc)
      );

      // Récupérer toutes les photos avec ces CRC
      filteredDuplicates = duplicates.filter(d => selectedDateCRCs.has(d.picture_crc));

      // Trier par CRC puis par date comme dans le cas non filtré
      filteredDuplicates.sort((a, b) => {
        if (a.picture_crc === b.picture_crc) {
          return new Date(b.createdat) - new Date(a.createdat);
        }
        return a.picture_crc.localeCompare(b.picture_crc);
      });
    } else {
      // Sans filtre de date, trier d'abord par CRC puis par date
      filteredDuplicates = [...duplicates].sort((a, b) => {
        if (a.picture_crc === b.picture_crc) {
          return new Date(b.createdat) - new Date(a.createdat);
        }
        return a.picture_crc.localeCompare(b.picture_crc);
      });
    }

    // Paginer les doublons filtrés
    const itemsPerPage = 20;
    const paginatedDuplicates = filteredDuplicates.slice(
      currentPage * itemsPerPage,
      (currentPage + 1) * itemsPerPage
    );

    return { paginatedDuplicates, chartData };
  }, [duplicates, currentPage, selectedDate]);

  // Reset la page courante quand on change de filtre
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedDate]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('Duplicates of {{username}}', { username })}
      size="xl"
    >
      <div className="p-4 dark:bg-gray-800">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {t('Duplicate photos detected')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('The following images have been identified as duplicates')}
          </p>
        </div>

        {/* Ajout de l'histogramme */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">
              {t('Duplicates distribution (last 365 days)')}
            </h4>
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                {t('Clear filter')}
              </button>
            )}
          </div>
          <div className="w-full h-[200px]">
            <BarChart width={800} height={200} data={chartData}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px'
                }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#fff' }}
                formatter={(value) => [`${value} ${t('duplicates')}`, t('Count')]}
                labelFormatter={(date) => dayjs(date).format('L')}
              />
              <Bar
                dataKey="count"
                fill="#8884d8"
                onClick={(data) => setSelectedDate(data.date)}
                cursor="pointer"
              />
            </BarChart>
          </div>
          {selectedDate && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('Showing duplicates from')} {dayjs(selectedDate).format('L')}
            </p>
          )}
        </div>

        {/* Grille des images */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {paginatedDuplicates.map((image, index) => (
            <div
              key={image.id}
              className="relative group aspect-w-1 aspect-h-1 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden"
            >
              <Picture pictureurl={image.pictureurl} className="object-cover w-full h-full" />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200" />
              <div className="absolute top-2 right-2 bg-black bg-opacity-80 dark:bg-opacity-60 rounded-full px-2 py-1 flex items-center justify-center">
                <span className="text-white text-xs">
                  #{image.picture_crc?.slice(-5)}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                <div className="text-white text-sm">
                  <div>Question: {image.question}</div>
                  {image.item && <div>Item: {image.item}</div>}
                  <div>{dayjs(image.createdat).format('L')}</div>
                  <div>{image.shopname}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex justify-center gap-2 items-center">
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg disabled:opacity-50"
          >
            {t('Previous')}
          </button>
          <span className="text-gray-700 dark:text-gray-300">
            {t('pageXOfTotal', { page: currentPage + 1, total: Math.ceil(duplicates.length / 20) })}
          </span>
          <button
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={paginatedDuplicates.length < 20}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg disabled:opacity-50"
          >
            {t('Next')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DuplicatesModal;